import { inject, Injectable } from "@angular/core";
import { UserClientEntityDto } from "@dtos/users-clients-entities.dto";
import { ApiService } from "@services/api.service";
import { EndPoints } from "@services/EndPoints";

//INTERFAZ PARA RESPUESTA PAGINADA
export interface PaginatedClientUsersResponse {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: UserClientEntityDto[];
}

@Injectable({ providedIn: 'root' })
export class ClientUsersProvider {

  private readonly api = inject(ApiService);

  /**
   * OBTENER USUARIOS DE CLIENTE CON SOPORTE PARA FILTRADO, BUSQUEDA, ORDENAMIENTO Y PAGINACION
   * @param queryParams - OBJETO CON TODOS LOS PARAMETROS DE CONSULTA
   * @returns RESPUESTA PAGINADA CON USUARIOS DE CLIENTE Y TOTAL
   */
  async getClientUsers(queryParams: Record<string, any> = {}): Promise<PaginatedClientUsersResponse | null> {
    try {
      //ESTABLECER VALORES POR DEFECTO SI NO SE PROPORCIONAN
      const params = {
        page: 1,
        page_size: 50,
        ...queryParams
      };

      //REALIZAR LA PETICION AL BACKEND
      return await this.api.get<PaginatedClientUsersResponse>({
        url: EndPoints.getClientUsers,
        queryParams: params
      });
    } catch (error) {
      console.error('Error al obtener usuarios de cliente:', error);
      return null;
    }
  }

  /**
   * OBTENER UN USUARIO DE CLIENTE POR ID
   * @param clientUserId - ID DEL USUARIO DE CLIENTE
   * @returns DATOS DEL USUARIO DE CLIENTE
   */
  async getClientUserById(clientUserId: number): Promise<UserClientEntityDto | null> {
    try {
      return await this.api.get<UserClientEntityDto>({
        url: EndPoints.getClientUser.replace('{clientUserId}', String(clientUserId))
      });
    } catch (error) {
      console.error('Error al obtener usuario de cliente por ID:', error);
      return null;
    }
  }

  /**
   * CREAR UN NUEVO USUARIO DE CLIENTE
   * @param userData - DATOS DEL USUARIO DE CLIENTE A CREAR
   * @param clientIds - ARRAY DE IDs DE CLIENTES/ENTIDADES A ASIGNAR
   * @param password - CONTRASEÑA DEL USUARIO
   * @returns USUARIO DE CLIENTE CREADO
   */
  async createClientUser(userData: Partial<UserClientEntityDto>, clientIds?: number[], password?: string): Promise<UserClientEntityDto | null> {
    try {
      const formParams: Record<string, any> = {
        name: userData.name || '',
        last_name: userData.last_name || '',
        username: userData.email || '',
        email: userData.email || '',
        phone: userData.phone || '',
        image: '',
        is_active: userData.is_active ?? true,
        is_superadmin: userData.is_superadmin ?? false,
        new_transport_notification: userData.new_transport_notification ?? true,
        driver_assigned_notification: userData.driver_assigned_notification ?? true,
        transport_finished_notification: userData.transport_finished_notification ?? true,
        cancelled_transport_notification: userData.cancelled_transport_notification ?? true,
        notify_client_users: userData.notify_client_users ?? true,
        notify_subclient_users: userData.notify_subclient_users ?? true,
        emails_to_notify: Array.isArray(userData.emails_to_notify) ? userData.emails_to_notify.join(',') : ''
      };

      // AGREGAR PASSWORD SI EXISTE
      if (password) {
        formParams['password'] = password;
      }

      // AGREGAR CLIENT_IDS COMO STRING SEPARADO POR COMAS
      if (clientIds && clientIds.length > 0) {
        formParams['client_ids'] = clientIds.join(',');
      }

      // AGREGAR CLIENT_GROUP SI EXISTE
      if (userData.client_group_name !== undefined) {
        formParams['client_group'] = userData.client_group_name;
      }

      return await this.api.post<UserClientEntityDto>({
        url: EndPoints.createClientUser,
        formParams
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * ACTUALIZAR UN USUARIO DE CLIENTE EXISTENTE
   * @param clientUserId - ID DEL USUARIO DE CLIENTE A ACTUALIZAR
   * @param userData - DATOS DEL USUARIO DE CLIENTE A ACTUALIZAR
   * @param clientIds - ARRAY DE IDs DE CLIENTES/ENTIDADES A ASIGNAR
   * @param password - CONTRASEÑA DEL USUARIO (OPCIONAL)
   * @returns USUARIO DE CLIENTE ACTUALIZADO
   */
  async updateClientUser(clientUserId: number, userData: Partial<UserClientEntityDto>, clientIds?: number[], password?: string): Promise<UserClientEntityDto | null> {
    try {
      const formParams: Record<string, any> = {
        image: ''
      };

      // AGREGAR SOLO LOS CAMPOS QUE ESTÁN DEFINIDOS
      if (userData.name !== undefined) formParams['name'] = userData.name;
      if (userData.last_name !== undefined) formParams['last_name'] = userData.last_name;
      if (userData.email !== undefined) {
        formParams['email'] = userData.email;
        formParams['username'] = userData.email;
      }
      if (userData.phone !== undefined) formParams['phone'] = userData.phone;
      if (userData.is_active !== undefined) formParams['is_active'] = userData.is_active;
      if (userData.is_superadmin !== undefined) formParams['is_superadmin'] = userData.is_superadmin;
      if (userData.new_transport_notification !== undefined) formParams['new_transport_notification'] = userData.new_transport_notification;
      if (userData.driver_assigned_notification !== undefined) formParams['driver_assigned_notification'] = userData.driver_assigned_notification;
      if (userData.transport_finished_notification !== undefined) formParams['transport_finished_notification'] = userData.transport_finished_notification;
      if (userData.cancelled_transport_notification !== undefined) formParams['cancelled_transport_notification'] = userData.cancelled_transport_notification;
      if (userData.notify_client_users !== undefined) formParams['notify_client_users'] = userData.notify_client_users;
      if (userData.notify_subclient_users !== undefined) formParams['notify_subclient_users'] = userData.notify_subclient_users;
      if (userData.emails_to_notify !== undefined) {
        formParams['emails_to_notify'] = Array.isArray(userData.emails_to_notify) ? userData.emails_to_notify.join(',') : '';
      }

      // AGREGAR PASSWORD SI EXISTE
      if (password) {
        formParams['password'] = password;
      }

      // AGREGAR CLIENT_IDS COMO STRING SEPARADO POR COMAS
      if (clientIds !== undefined) {
        formParams['client_ids'] = clientIds.length > 0 ? clientIds.join(',') : '';
      }

      // AGREGAR CLIENT_GROUP SI EXISTE
      if (userData.client_group_name !== undefined) {
        formParams['client_group'] = userData.client_group_name;
      }

      return await this.api.patch<UserClientEntityDto>({
        url: EndPoints.updateClientUser.replace('{clientUserId}', String(clientUserId)),
        formParams
      });
    } catch (error) {
      console.error('Error al actualizar usuario de cliente:', error);
      return null;
    }
  }

  /**
   * ELIMINAR UN USUARIO DE CLIENTE
   * @param clientUserId - ID DEL USUARIO DE CLIENTE A ELIMINAR
   * @returns TRUE SI SE ELIMINO CORRECTAMENTE, FALSE SI HUBO ERROR
   */
  async deleteClientUser(clientUserId: number): Promise<boolean> {
    try {
      await this.api.delete({
        url: EndPoints.deleteClientUser.replace('{clientUserId}', String(clientUserId))
      });
      return true;
    } catch (error) {
      console.error('Error al eliminar usuario de cliente:', error);
      return false;
    }
  }

  /**
   * EXPORTAR USUARIOS DE CLIENTE A CSV
   * DESCARGA UN ARCHIVO CSV CON TODOS LOS USUARIOS DE CLIENTE
   * @returns BLOB DEL ARCHIVO CSV
   */
  async exportClientUsersCSV(): Promise<Blob | null> {
    try {
      const blob = await this.api.downloadFile({
        url: EndPoints.exportClientUsersCSV
      });

      // CREAR UN ENLACE TEMPORAL PARA DESCARGAR EL ARCHIVO
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'client-users.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return blob;
    } catch (error) {
      console.error('Error al exportar usuarios de cliente a CSV:', error);
      return null;
    }
  }
}
